import { hasYesOrNo } from 'models/validate';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { DEFAULT_EARLIEST, DEFAULT_LATEST } from 'constants/dateLimits';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { setToStartOfMonth } from 'helpers/date';
import { ListModel } from 'forms/shared/types/list';
import { SanitizeDateRange } from 'forms/shared/validators/models/dateRange/instance';
import { isPVQForm } from 'helpers/form';
import { DeepSafePartial } from 'types/types';
import { IdentificationOtherNamesModel, IdentificationOtherNameItemModel } from './types';

export const hasIdentificationOtherNamesDuplicates = (value: DeepSafePartial<ListModel<IdentificationOtherNameItemModel>>) => {
  let result = false;
  if (value?.items) {
    const cleanItems = value.items?.map((x) => x?.Item);
    // remove the type pollution for validation from the date range
    // there may be more type pollution possible that wasn't available during the testing
    cleanItems.forEach((v) => {
      const today = new Date();

      if (v) {
        const dates = SanitizeDateRange(v?.DatesUsed);
        if (dates?.present && dates?.to) {
          dates.to.day = today.getDate().toString();
          dates.to.year = today.getFullYear().toString();
          dates.to.month = (today.getMonth() + 1).toString();
        }
        v.DatesUsed = { ...dates }; // eslint-disable-line no-param-reassign
      }
    });

    // compare items on the list case insensitive and ignore reason differences.
    const items = cleanItems.map((x) => JSON.stringify({ ...x, Reason: '' }).toLowerCase());
    const uniqueItems = new Set(items);
    result = !(items.length === uniqueItems.size);
  }
  return result;
};

export const identificationOtherNameItemValidator: ValidateJSModel<IdentificationOtherNameItemModel> = {
  Name: (value, attributes, attributeName, options) => (
    isPVQForm(options?.formType) ? {
      presence: true,
      model: { validator: pvqNameModelValidator },
    } : {
      presence: true,
      model: { validator: nameModelValidator },
    }),
  MaidenName: (value, attributes, attributeName, options) => (
    isPVQForm(options?.formType) ? {} : { presence: true, hasValue: { validator: hasYesOrNo } }
  ),
  DatesUsed: (value, attributes, attributeName, options) => ({
    presence: true,
    daterange: {
      earliest: setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
      latest: DEFAULT_LATEST,
      tooEarly: 'error.date.min',
    },
  }),
  Reason: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) || options?.requireIdentificationOtherNameExplanation === false) {
      return {};
    }

    return { hasValue: true };
  },
  Comments: {},
};

const identificationOtherNamesValidator: ValidateJSModel<IdentificationOtherNamesModel> = {

  HasOtherNames: { presence: true, hasValue: { validator: hasYesOrNo } },
  List: (value, attributes) => {
    if (attributes?.HasOtherNames?.value === 'No') { return {}; }
    if (hasIdentificationOtherNamesDuplicates(value)) {
      return {
        presence: true,
        length: { is: -2, message: 'error.list.duplicate' },
        accordion: { validator: identificationOtherNameItemValidator },
      };
    }
    return {
      presence: true,
      accordion: { validator: identificationOtherNameItemValidator },
    };
  },
  Comments: {},
  SummaryComments: {},
};

export default identificationOtherNamesValidator;
